import { useState, useCallback } from "react";
import { useWedding } from "../contexts/wedding-context";
import { useAuth } from "../contexts/auth-context";
import { createApi } from "../lib/api";

/**
 * `form` → `otp` → (oturum açılır) tek akış. Terminal durumlar kendi kartlarını
 * gösterir; `closed`/`cap_reached` sunucudan gelir (form önden de kontrol eder,
 * ama son söz sunucunundur).
 */
export type RegisterStep =
  | "form"
  | "otp"
  | "done"
  | "already_registered"
  | "closed"
  | "cap_reached"
  /** Düğünün misafir kotası dolu (paket limiti + ekstra paketler; 2026-10-15). */
  | "guest_limit";

interface RegisterState {
  step: RegisterStep;
  phone: string;
  verificationSid: string | undefined;
  loading: boolean;
  /** Ham sunucu kodu/mesajı — kullanıcıya gösterilecek metne form çevirir. */
  error: string;
}

interface UseQrRegisterReturn extends RegisterState {
  register: (name: string, phone: string, turnstileToken: string, smsConsent?: boolean) => Promise<void>;
  verify: (code: string) => Promise<void>;
  reset: () => void;
}

const INITIAL: RegisterState = {
  step: "form",
  phone: "",
  verificationSid: undefined,
  loading: false,
  error: "",
};

export function useQrRegister(): UseQrRegisterReturn {
  const { slug } = useWedding();
  const { verifyCode, markAuthenticated } = useAuth();

  const [state, setState] = useState<RegisterState>(INITIAL);

  const register = useCallback(async (name: string, phone: string, turnstileToken: string, smsConsent?: boolean) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const api = createApi(slug);
      const res = await api.post<unknown>("/auth/register", { name, phone, turnstileToken, smsConsent });
      const raw = res as unknown as {
        ok: boolean;
        step?: string;
        authed?: boolean;
        verificationSid?: string;
        alreadyRegistered?: boolean;
        guest?: { name: string | null; phone: string };
      };

      if (raw.alreadyRegistered) {
        setState((s) => ({ ...s, step: "already_registered", phone, loading: false }));
        return;
      }
      // OTP kapalı: sunucu oturumu açtı. Reload YOK — auth context'i işaretleyince
      // JoinPage kendi yönlendirmesini yapar (premium tema loader'ı tekrar oynamaz).
      if (raw.authed) {
        setState((s) => ({ ...s, step: "done", phone, loading: false }));
        markAuthenticated(raw.guest ?? { name, phone });
        return;
      }
      if (raw.step === "verify") {
        setState((s) => ({
          ...s, step: "otp", phone, verificationSid: raw.verificationSid, loading: false,
        }));
        return;
      }
      setState((s) => ({ ...s, loading: false, error: "unknown_response" }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "registration_failed";
      if (msg === "registration_closed") {
        setState((s) => ({ ...s, step: "closed", loading: false, error: "" }));
      } else if (msg === "registration_cap_reached") {
        setState((s) => ({ ...s, step: "cap_reached", loading: false, error: "" }));
      } else if (msg === "guest_limit_reached") {
        setState((s) => ({ ...s, step: "guest_limit", loading: false, error: "" }));
      } else {
        setState((s) => ({ ...s, loading: false, error: msg }));
      }
    }
  }, [slug, markAuthenticated]);

  const verify = useCallback(async (code: string) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      // Auth context üzerinden: oturum durumu tek yerde güncellenir ve
      // `isAuthenticated` true olur olmaz JoinPage düğün ana sayfasına gider.
      await verifyCode(state.phone, code, state.verificationSid);
      setState((s) => ({ ...s, step: "done", loading: false }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "verification_failed";
      if (msg === "registration_closed") {
        setState((s) => ({ ...s, step: "closed", loading: false, error: "" }));
        return;
      }
      // Kod gönderildikten sonra kota dolmuş olabilir (aynı anda kaydolanlar).
      if (msg === "guest_limit_reached") {
        setState((s) => ({ ...s, step: "guest_limit", loading: false, error: "" }));
        return;
      }
      setState((s) => ({ ...s, loading: false, error: msg }));
    }
  }, [verifyCode, state.phone, state.verificationSid]);

  const reset = useCallback(() => setState(INITIAL), []);

  return { ...state, register, verify, reset };
}
